import {
  getDefaultValue,
  Field,
  Validation,
  FieldControllerType,
  ConditionType,
} from '@gemini/lux-ds-pms';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { usePatientStore } from '@store/patientStore';
import { FormIds } from '@utils/enums/FormIds.enum';
import { IntegrationModes } from '@utils/enums/IntegrationModes.enum';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import { convertStringToFieldControllerType } from '@utils/functions';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const useMergeForm = () => {
  const { t } = useTranslation();
  const formConfigs = useOnlineBookingStore(state => state.formConfigs);
  const currentPatient = useOnlineBookingStore(state => state.currentPatient);
  const patients = usePatientStore(state => state.patients);
  const integrationMode = useOnlineBookingStore(state => state.integrationMode);

  const insuranceType = useMemo(
    () => patients[currentPatient]?.insurance?.type,
    [currentPatient, patients],
  );

  const validationRequired: Validation = useMemo(() => {
    return {
      type: 'required',
      value: true,
      errorMsgKey: 'fieldsLabel.errors.isRequired',
    };
  }, []);
  const validationTextLength: Validation = useMemo(() => {
    return {
      type: 'maxLength',
      value: 300,
      errorMsgKey: 'fieldsLabel.errors.customMaxLength',
    };
  }, []);

  const addValidation = (field: Field): Field => ({
    ...field,
    validations: field.validations
      ? [...field.validations, validationRequired]
      : [validationRequired],
  });

  const setHidden = (field: Field): Field => ({
    ...field,
    visibility: false,
  });

  //PATIENT DETAILS FIELDS CHECKS
  const isInterdepPhoneEmailPatientDetails: boolean = useMemo(() => {
    const patientDetailsConfig = formConfigs?.patientDetails;

    return (
      patientDetailsConfig?.email === StaticValues.INTERDEP_PHONENUMBER_EMAIL &&
      patientDetailsConfig?.phoneNumber?.phoneNumber ===
        StaticValues.INTERDEP_PHONENUMBER_EMAIL
    );
  }, [formConfigs]);

  const isPhoneTypeHiddenPatientDetails = useMemo(() => {
    const patientDetailsConfig = formConfigs?.patientDetails;
    if (!patientDetailsConfig) return false;

    return (
      getDefaultValue('patientDetails.phoneNumber.type', {
        patientDetails: patientDetailsConfig,
      }) === StaticValues.FIELD_STATUS_HIDDEN
    );
  }, [formConfigs]);

  //CONTACT DETAILS FIELDS CHECKS
  const isInterdepPhoneEmailContactsDetails: boolean = useMemo(() => {
    const contactsDetailsConfig = formConfigs?.contactsDetails;

    return (
      contactsDetailsConfig?.email ===
        StaticValues.INTERDEP_PHONENUMBER_EMAIL &&
      contactsDetailsConfig?.phoneNumber?.phoneNumber ===
        StaticValues.INTERDEP_PHONENUMBER_EMAIL
    );
  }, [formConfigs]);

  const isPhoneTypeHiddenContactsDetails = useMemo(() => {
    const contactsDetailsConfig = formConfigs?.contactsDetails;
    if (!contactsDetailsConfig) return false;

    return (
      getDefaultValue('contactsDetails.phoneNumber.type', {
        contactsDetails: contactsDetailsConfig,
      }) === StaticValues.FIELD_STATUS_HIDDEN
    );
  }, [formConfigs]);

  const mergePatientDetails = (fields: Field[]): Field[] => {
    if (formConfigs) {
      const patientDetailsConfig = formConfigs.patientDetails;

      const mergedForm: Field[] = fields.map(f => {
        const fieldConfig = getDefaultValue(f.id, {
          patientDetails: patientDetailsConfig,
        });

        if (
          !formConfigs.showPatientInsurance &&
          (f.id.includes('patientDetails.visionInsuranceDetails') ||
            f.id.includes('patientDetails.medicalInsuranceDetails')) &&
          !f.children
        ) {
          if (insuranceType === StaticValues.INSURANCE_TYPE_VISION) {
            if (
              f.id !==
                'patientDetails.visionInsuranceDetails.visionInsurance' &&
              f.id !==
                'patientDetails.visionInsuranceDetails.visionInsuranceMemberId'
            ) {
              return setHidden(f);
            }
          } else {
            if (
              f.id !==
                'patientDetails.medicalInsuranceDetails.medicalInsurance' &&
              f.id !==
                'patientDetails.medicalInsuranceDetails.medicalInsuranceMemberId'
            ) {
              return setHidden(f);
            }
          }
        }

        if (f.children) {
          return {
            ...f,
            children: mergePatientDetails(f.children),
          };
        }

        // INTERDEP_PHONENUMBER_EMAIL -> mutua interdipendenza
        if (
          (f.id === 'patientDetails.email' ||
            f.id === 'patientDetails.phoneNumber.phoneNumber') &&
          isInterdepPhoneEmailPatientDetails
        ) {
          if (f.id === 'patientDetails.email') {
            // conditions di base
            const conditions: { idRef: string; type: ConditionType }[] = [
              {
                idRef: 'patientDetails.phoneNumber.phoneNumber',
                type: ConditionType.NOT_EMPTY,
              },
            ];

            // aggiungo la condizione sul type SOLO se non è hidden
            if (!isPhoneTypeHiddenPatientDetails) {
              conditions.push({
                idRef: 'patientDetails.phoneNumber.type',
                type: ConditionType.NOT_EMPTY,
              });
            }

            const interdepField = {
              ...f,
              interdepCondition: {
                type: 'AND',
                toEvaluateIf: {
                  type: 'required',
                  value: false,
                  errorMsgKey: 'error',
                },
                conditions,
              },
            } as Field;

            return addValidation(interdepField);
          }

          if (f.id === 'patientDetails.phoneNumber.phoneNumber') {
            const interdepField = {
              ...f,
              interdepCondition: {
                type: 'AND',
                toEvaluateIf: {
                  type: 'required',
                  value: false,
                  errorMsgKey: 'error',
                },
                conditions: [
                  {
                    idRef: 'patientDetails.email',
                    type: ConditionType.NOT_EMPTY,
                  },
                  {
                    idRef: 'patientDetails.phoneNumber.phoneNumber',
                    type: ConditionType.EMPTY,
                  },
                ],
              },
            } as Field;
            //interdep condition + add validation in order to make the "empty" check
            return addValidation(interdepField);
          }
        }

        if (fieldConfig === StaticValues.FIELD_STATUS_MANDATORY) {
          return addValidation(f);
        } else if (fieldConfig === StaticValues.FIELD_STATUS_HIDDEN) {
          return setHidden(f);
        } else {
          return f;
        }
      });

      return mergedForm;
    } else {
      return fields;
    }
  };

  const getFormChildren = (formId: FormIds) => {
    switch (formId) {
      case FormIds.CustomFieldsPatient:
        return formConfigs?.patientDetails.customFieldsPatient;
      case FormIds.CustomFieldsInsurance:
        return formConfigs?.patientDetails.customFieldsInsurance;
      case FormIds.CustomPage:
        return formConfigs?.customPage;
      default:
        return null;
    }
  };

  const getCustomFieldsValidations = useCallback(
    (status: StaticValues, type?: string) => {
      // type is absent when we get customFields for patient or insurance (always text)
      if (status === StaticValues.FIELD_STATUS_MANDATORY) {
        if (!type || type.toLowerCase() === 'text')
          return [validationRequired, validationTextLength];
        else return [validationRequired];
      } else {
        if (!type || type.toLowerCase() === 'text')
          return [validationTextLength];
        else return undefined;
      }
    },
    [validationRequired, validationTextLength],
  );

  const createCustomFields = (
    prefix: string,
    formId: FormIds,
    forcingTextArea = false,
  ): Field[] => {
    if (formConfigs) {
      const customFieldsConfig = getFormChildren(formId);
      if (
        customFieldsConfig &&
        customFieldsConfig.show &&
        customFieldsConfig.children?.length > 0
      ) {
        return customFieldsConfig.children.map((c, i) => ({
          id: prefix + '.field' + (i + 1),
          size: {
            xs: 12,
            md: 12,
            lg: 12,
          },
          labelKey: c.label,
          type: forcingTextArea
            ? FieldControllerType.TEXTAREA
            : !c.type || c.type?.toLowerCase() === 'text'
              ? FieldControllerType.TEXTAREA
              : convertStringToFieldControllerType(c.type),
          visibility:
            c.status !== StaticValues.FIELD_STATUS_HIDDEN ? true : false,
          validations: getCustomFieldsValidations(c.status, c.type),
          otherOptions: {
            textArea: {
              maxLength: '300',
              limit: 300,
              minRows: '3',
              resizable: true,
            },
          },
          options:
            c.type && c.type.toLowerCase() === 'radio'
              ? [
                  {
                    key: 'yes',
                    label: 'Yes',
                  },
                  { key: 'no', label: 'No' },
                ]
              : undefined,
        }));
      } else {
        return [];
      }
    } else {
      return [];
    }
  };

  const mergeContactsDetails = (fields: Field[]): Field[] => {
    if (formConfigs) {
      const contactsDetailsConfig = formConfigs.contactsDetails;

      const contactsDetailsFields: Field[] = fields.map(f => {
        const fieldConfig = getDefaultValue(f.id, {
          contactsDetails: contactsDetailsConfig,
        });

        if (f.children) {
          return {
            ...f,
            children: mergeContactsDetails(f.children),
          };
        }
        // INTERDEP_PHONENUMBER_EMAIL -> mutua interdipendenza (contactsDetails)
        if (
          (f.id === 'contactsDetails.email' ||
            f.id === 'contactsDetails.phoneNumber.phoneNumber') &&
          isInterdepPhoneEmailContactsDetails
        ) {
          if (f.id === 'contactsDetails.email') {
            const conditions: { idRef: string; type: ConditionType }[] = [
              {
                idRef: 'contactsDetails.phoneNumber.phoneNumber',
                type: ConditionType.NOT_EMPTY,
              },
            ];

            if (!isPhoneTypeHiddenContactsDetails) {
              conditions.push({
                idRef: 'contactsDetails.phoneNumber.type',
                type: ConditionType.NOT_EMPTY,
              });
            }

            const interdepField = {
              ...f,
              interdepCondition: {
                type: 'AND',
                toEvaluateIf: {
                  type: 'required',
                  value: false,
                  errorMsgKey: 'error',
                },
                conditions,
              },
            } as Field;

            return addValidation(interdepField);
          }

          if (f.id === 'contactsDetails.phoneNumber.phoneNumber') {
            const interdepField = {
              ...f,
              interdepCondition: {
                type: 'AND',
                toEvaluateIf: {
                  type: 'required',
                  value: false,
                  errorMsgKey: 'error',
                },
                conditions: [
                  {
                    idRef: 'contactsDetails.email',
                    type: ConditionType.NOT_EMPTY,
                  },
                  {
                    idRef: 'contactsDetails.phoneNumber.phoneNumber',
                    type: ConditionType.EMPTY,
                  },
                ],
              },
            } as Field;

            return addValidation(interdepField);
          }
        }

        if (fieldConfig === StaticValues.FIELD_STATUS_MANDATORY) {
          return addValidation(f);
        } else if (fieldConfig === StaticValues.FIELD_STATUS_HIDDEN) {
          return setHidden(f);
        } else {
          return f;
        }
      });
      return contactsDetailsFields;
    } else return fields;
  };

  const mergeCustomFieldsMarketing = (
    fields: Field[],
    isBranded?: boolean,
  ): Field[] => {
    const contactsDetailsFields = [...fields];

    if (formConfigs) {
      let customFieldsMarketing =
        formConfigs.customFieldsMarketing.children || [];
      if (integrationMode === IntegrationModes.STANDALONE) {
        // Remove EMAIL optin if in Standalone mode
        customFieldsMarketing = customFieldsMarketing.filter(
          c => c.id !== 'EMAIL',
        );
      }

      const marketingFields = [
        { id: 'contactsDetails.storeCustomFieldsMarketing.field1' },
        { id: 'contactsDetails.storeCustomFieldsMarketing.field2' },
        { id: 'contactsDetails.storeCustomFieldsMarketing.field3' },
      ];

      marketingFields.forEach((marketingField, i) => {
        const fieldConfig = customFieldsMarketing[i];
        if (fieldConfig) {
          const field: Field = {
            id: marketingField.id,
            type: fieldConfig.type
              ? convertStringToFieldControllerType(fieldConfig.type)
              : FieldControllerType.TEXT,
            visibility: fieldConfig.status !== StaticValues.FIELD_STATUS_HIDDEN,
            //if is branded, we should try to translate the label. Otherwise, just show it without processing it
            labelKey: isBranded
              ? t(`contactDetailsPage.${fieldConfig.label}`) ||
                fieldConfig.label
              : fieldConfig.label,
            otherOptions: {
              toggle: {
                labelIsHtml: true,
              },
            },
            size: {
              xs: 12,
              md: 12,
              lg: 12,
            },
            validations: [],
            // ...(i !== marketingFields.length - 1 && {
            //   style: { marginBottom: '-1rem' },
            // }),
          };
          if (fieldConfig.status === StaticValues.FIELD_STATUS_MANDATORY) {
            field.validations?.push(validationRequired);
          }
          contactsDetailsFields.push(field);
        }
      });
    }

    return contactsDetailsFields;
  };

  return {
    mergePatientDetails,
    createCustomFields,
    mergeContactsDetails,
    mergeCustomFieldsMarketing,
  };
};

export default useMergeForm;
